// Infrastructure an agent planned, and whether it ever applied it.
//
//   AWS_PROFILE=… tofu plan
//   > Plan: 91 to add, 15 to change, 1 to destroy.
//
// `Plan: N to add, M to change, K to destroy` is PLATFORM schema — Terraform and
// OpenTofu print it identically for every stack on earth — so the size of a
// proposed change accumulates without knowing anything about the modules involved.
//
// The reading is the gap between PLAN and APPLY. A plan is a statement of intent
// and a promise about blast radius; an apply is the act. A session that planned 91
// additions and never applied left a decision open, and one that applied without a
// plan in the trace took an action nobody can size afterwards.

import { flagOf, positionalsOf, shellStatements } from "../../src/mine/statements.js";

const TOOLS = /^(?:tofu|terraform|pulumi|cdk|helm|ansible-playbook)$/i;
const PLAN_RE = /\bPlan:\s*(\d+)\s+to add,\s*(\d+)\s+to change,\s*(\d+)\s+to destroy/i;
const APPLIED_RE = /\bApply complete!\s*Resources:\s*(\d+)\s+added,\s*(\d+)\s+changed,\s*(\d+)\s+destroyed/i;
const NOOP_RE = /No changes\.|Your infrastructure matches the configuration|Plan:\s*0 to add, 0 to change, 0 to destroy/i;

/** The stack being acted on: a chdir target, a workspace, or the module path. */
export function stackOf(st) {
  const chdir = flagOf(st.args, "-chdir", "--chdir");
  if (chdir) return chdir.replace(/^\.\//, "");
  const bare = positionalsOf(st.args, new Set(["-var", "-var-file", "-target", "-out", "-state", "-f", "--values", "-n", "--namespace"]));
  const pathish = bare.find((t) => /\//.test(t) && !/^-/.test(t));
  return pathish ? pathish.replace(/^\.\//, "").replace(/\/$/, "") : null;
}

export function extractIacOps(turns) {
  const ops = [];
  for (const st of shellStatements(turns)) {
    if (!TOOLS.test(st.program)) continue;
    const bare = positionalsOf(st.args, new Set(["-chdir", "-var", "-var-file", "-target", "-out", "-f", "--values"]));
    const verb = (bare[0] || "").toLowerCase();
    const kind = /^(?:plan|diff|preview)$/.test(verb) ? "plan"
      : /^(?:apply|deploy|up|install|upgrade)$/.test(verb) ? "apply"
      : /^(?:destroy|down|uninstall)$/.test(verb) ? "destroy"
      : /^(?:fmt|validate|show|state|output|version|init|providers)$/.test(verb) ? "check" : null;
    if (!kind) continue;

    const out = st.output || "";
    const plan = PLAN_RE.exec(out);
    const applied = APPLIED_RE.exec(out);
    const counts = plan ? { add: +plan[1], change: +plan[2], destroy: +plan[3] }
      : applied ? { add: +applied[1], change: +applied[2], destroy: +applied[3] } : null;

    ops.push({
      key: st.key,
      // A stack the command did not name is the working directory, which the trace
      // does not carry — said plainly rather than guessed.
      stack: stackOf(st) || "the working directory",
      tool: st.program,
      verb, kind,
      counts,
      touched: counts ? counts.add + counts.change + counts.destroy : null,
      destroys: counts?.destroy ?? null,
      outcome: !out ? "unrecorded"
        : NOOP_RE.test(out) ? "no-change"
        : applied ? "applied"
        : plan ? "planned"
        : /\bError:|\bfailed\b/i.test(out) ? "failed" : "answered",
      output: st.output,
      outputStatus: st.outputStatus,
      ok: st.ok,
      steps: [{ kind, cmd: st.text.replace(/\s+/g, " ").slice(0, 200) }],
      checks: [{ kind, tool: stackOf(st) || "the working directory",
        scope: counts ? [`${counts.add} add`, `${counts.change} change`, `${counts.destroy} destroy`] : [],
        outcome: !out ? "unrecorded" : NOOP_RE.test(out) ? "no change" : applied ? "applied" : plan ? "planned only" : "answered" }],
    });
  }
  return ops;
}

export function describeMissing(op) {
  if (op.outcome === "no-change") return "the stack already matched the configuration — nothing was proposed";
  if (op.outcome === "failed") return "the run failed, so neither a plan nor a change came out of it";
  switch (op.outputStatus) {
    case "backgrounded": return "ran in the background — the plan never entered this trace";
    case "none": return "ran and captured no output, so the size of the change is not recoverable";
    default: return null;
  }
}
